import { LOCAL_STORAGE_KEYS } from 'config/Vars'

import getMultiFilters from 'modules/core/components/MultiFilters/hooks/utils/getMultiFilters'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { dateToString, dateUIToBackend, stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import {
  formatCustomFilters,
  getCustomFilters,
  isPastEmployees,
} from 'modules/shift_management/components/ShiftManagement/Content/_filters/customFilters'
import useApplyFilters from 'modules/shift_management/hooks/useApplyFilters'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import { useShiftsRows } from 'modules/shift_management/hooks/useShiftsRows'
import Api from 'modules/shift_management/lib/Api'
import type { IExportScheduleFormValues } from 'modules/shift_management/lib/forms/ExportScheduleForm'
import ExportScheduleForm from 'modules/shift_management/lib/forms/ExportScheduleForm'

interface ITrackScheduleExportSubmit {
  view: string
  view_type_by: string
  from_date: string
  to_date: string
  number_of_filters: number
  export_by: string
  exclude_draft_shifts: boolean
}
const trackScheduleExportSubmit = function (params: ITrackScheduleExportSubmit) {
  track(trackingEvents.shiftManagement.requestScheduleExportSubmit, { ...params })
}

export const useExportScheduleForm = () => {
  return useLazyRef<ExportScheduleForm>(
    () => new ExportScheduleForm({ exclude_drafts: false, export_by: 'current_filters' })
  ).current
}

export const useShiftManagementSelectedDateRange = () => {
  const [{ view }] = useSchedule()
  const { startAt, endAt } = view

  return {
    startAt: dateToString(stringToDate(startAt, 'backend'), 'ui'),
    endAt: dateToString(stringToDate(endAt, 'backend'), 'ui'),
  }
}

export const useShiftManagementHasConflifcts = () => {
  const [{ conflictsCount }] = useSchedule()

  return !!conflictsCount
}

const useFilteredEmployeeIds = () => {
  const [{ shiftsRows }] = useShiftsRows()
  const { getFilteredEmployees } = useApplyFilters(shiftsRows)

  return () => {
    return getFilteredEmployees().map((employee) => employee.id)
  }
}

export const useCreateExportSchedule = (onSuccess?: () => void) => {
  const { startAt, endAt } = useShiftManagementSelectedDateRange()
  const getFilteredEmployeeeIds = useFilteredEmployeeIds()
  const [{ view }] = useSchedule()

  const { running: submitting, performAction } = useAsyncAction(
    async (formValues: IExportScheduleFormValues) => {
      const { filters: multiFilters, logicOperator } = getMultiFilters(
        LOCAL_STORAGE_KEYS.shiftEmployees
      )
      const custom_filters = getCustomFilters(multiFilters)
      const terminated = isPastEmployees(multiFilters)

      trackScheduleExportSubmit({
        view: view.viewType,
        view_type_by: view.groupType,
        from_date: startAt,
        to_date: endAt,
        number_of_filters: multiFilters.length,
        export_by: formValues.export_by,
        exclude_draft_shifts: formValues.exclude_drafts,
      })

      const payload = {
        ...formValues,
        start_at: dateUIToBackend(startAt),
        end_at: dateUIToBackend(endAt),
        employee_ids: getFilteredEmployeeeIds(),
        logic_operator: logicOperator.get('logic_operator').value,
        custom_filters: formatCustomFilters(custom_filters),
        terminated: terminated,
      }

      await Api.exportShiftsSchedule(payload)

      if (onSuccess) {
        onSuccess()
      }
    },
    {
      successMessage: i18n.t('shift_management.export_shifts.success_toast_message'),
    }
  )

  return {
    onSubmit: performAction,
    submitting: submitting,
  }
}
